<script setup lang="ts">
import CustomButton from '@/components/CustomButton.vue';
import { useEmployeeStore } from '@/stores/employee';
import { useNotificationStore } from '@/stores/notification';
import type { Employee } from '@/types/employee';
import type { NotificationForm, Recipient } from '@/types/notification';
import { processErrorWithFeedback } from '@/utils/error';
import toast from '@/utils/toast';
import { required } from '@/utils/validation';
import { onMounted, ref, useTemplateRef } from 'vue';

const notificationStore = useNotificationStore();
const employeeStore = useEmployeeStore();

const notification = ref({} as NotificationForm);
const employees = ref<Employee[]>([]);
const isLoading = ref(false);

const form = useTemplateRef('form');

async function createNotification() {
    const formValidation = await form.value?.validate();
    if (!formValidation?.valid) return;

    try {
        const recipients = notification.value.recipients.map((employeeId) => ({ employeeId }) as Recipient);
        const result = await notificationStore.create({ ...notification.value, recipients });

        if (result.status != 201) {
            processErrorWithFeedback('Notificatie kon niet worden aangemaakt', result.message);
        }

        form.value?.reset();
        toast.success('NotificationSent');
    } finally {
        isLoading.value = false;
    }
}

onMounted(async () => {
    employees.value = await employeeStore.fetch();
});
</script>

<template>
    <v-row class="align-start justify-center">
        <v-col lg="6">
            <v-form ref="form">
                <v-text-field v-model="notification.title" label="Onderwerp" :rules="[required]" required />
                <v-autocomplete
                    v-model="notification.recipients"
                    label="Medewerkers"
                    :items="employees"
                    item-title="fullName"
                    item-value="_id"
                    multiple
                    :rules="[required]"
                    required
                    clearable
                />
                <v-textarea v-model="notification.message" label="Inhoud" :rules="[required]" required />
            </v-form>

            <CustomButton class="mt-2" @click="createNotification" text="Aanmaken" :loading="isLoading" />
        </v-col>
    </v-row>
</template>
